import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles, Layers, Search, CheckCircle2, ArrowRight, X, FileText } from 'lucide-react';
import { exportBrandPriceListPDF } from '@/utils/exportBrandPdf';

export default function AsaExportBrandPdfModal({
  isOpen,
  onClose,
  activeBrand,
  botolList = [],
  tutupList = [],
  sprayList = [],
  masterBahanList = [],
  itemTambahanList = [],
  fullsetList = [],
}) {
  const [exportMode, setExportMode] = useState('all'); // 'all' | 'custom'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVariants, setSelectedVariants] = useState(() => {
    return new Set(activeBrand?.variants?.map(v => v.name) || []);
  });

  // Re-sync if activeBrand changes
  React.useEffect(() => {
    if (activeBrand?.variants?.length) {
      setSelectedVariants(new Set(activeBrand.variants.map(v => v.name)));
    }
  }, [activeBrand]);

  const allVariants = activeBrand?.variants || [];
  const totalVariantsCount = allVariants.length;

  const filteredVariants = useMemo(() => {
    if (!searchTerm.trim()) return allVariants;
    const q = searchTerm.toLowerCase();
    return allVariants.filter(v => (v.name || '').toLowerCase().includes(q));
  }, [allVariants, searchTerm]);

  const toggleVariant = (name) => {
    const next = new Set(selectedVariants);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    setSelectedVariants(next);
  };

  const handleSelectAll = () => {
    setSelectedVariants(new Set(allVariants.map(v => v.name)));
  };

  const handleDeselectAll = () => {
    setSelectedVariants(new Set());
  };

  const handleExport = () => {
    const chosenVariants = exportMode === 'all' ? 'all' : Array.from(selectedVariants);
    
    if (exportMode === 'custom' && chosenVariants.length === 0) {
      alert('Silakan pilih minimal 1 varian produk untuk diekspor ke PDF.');
      return;
    }

    onClose();

    // Directly generate & open aesthetic A4 PDF
    exportBrandPriceListPDF({
      activeBrand,
      botolList,
      tutupList,
      sprayList,
      masterBahanList,
      itemTambahanList,
      fullsetList,
      selectedVariantNames: chosenVariants,
    });
  };

  if (!isOpen || !activeBrand) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-[#E6D5B8] bg-white shadow-2xl rounded-3xl max-h-[92vh] flex flex-col my-auto">
        <div className="p-6 overflow-y-auto flex-1">
          
          {/* ASA Avatar & Header */}
          <div className="flex flex-col items-center justify-center text-center mb-4 shrink-0">
            <div className="relative mb-2.5">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-md relative z-10 bg-[#F9F5EC]">
                <img 
                  src="/asa-profile.png" 
                  alt="ASA AI" 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.src = '/logo.png'; }} 
                />
              </div>
              <div className="absolute inset-0 bg-[#8C6208] rounded-full blur-lg opacity-25 scale-125 animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full z-20"></div>
            </div>
            <h3 className="text-base font-black text-[#302621]">ASA AI</h3>
            <p className="text-[10px] text-emerald-600 font-bold tracking-widest uppercase">Asisten Ekspor Dokumen</p>
          </div>

          {/* Speech Bubble */}
          <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl shadow-2xs mb-5 relative text-center">
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-[#FFFDF9] border-t border-l border-[#E6D5B8] rotate-45"></div>
            <p className="text-xs text-[#302621] font-medium leading-relaxed relative z-10">
              "Halo! Sebelum saya buatkan dokumen <strong>PDF Daftar Harga A4</strong> untuk brand <strong>{activeBrand.name}</strong>, kamu mau mencantumkan semua varian atau mau memilih beberapa varian saja?"
            </p>
          </div>

          {/* Mode Selection Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {/* Card 1: Semua Varian */}
            <div
              onClick={() => setExportMode('all')}
              className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                exportMode === 'all'
                  ? 'border-[#8C6208] bg-[#F9F5EC]/70 shadow-xs'
                  : 'border-slate-200 hover:border-[#E6D5B8] bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="w-7 h-7 rounded-xl bg-[#8C6208]/10 text-[#8C6208] flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                  exportMode === 'all' ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                }`}>
                  {exportMode === 'all' && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-black text-[#302621]">Semua Varian</h4>
                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                  Cetak seluruh <strong>{totalVariantsCount} varian</strong> yang terdaftar pada brand ini.
                </p>
              </div>
            </div>

            {/* Card 2: Pilih Varian Tertentu */}
            <div
              onClick={() => setExportMode('custom')}
              className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between ${
                exportMode === 'custom'
                  ? 'border-[#8C6208] bg-[#F9F5EC]/70 shadow-xs'
                  : 'border-slate-200 hover:border-[#E6D5B8] bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="w-7 h-7 rounded-xl bg-[#8C6208]/10 text-[#8C6208] flex items-center justify-center">
                  <Layers className="w-4 h-4" />
                </div>
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                  exportMode === 'custom' ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                }`}>
                  {exportMode === 'custom' && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-black text-[#302621]">Pilih Varian</h4>
                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                  Pilih sendiri varian mana saja yang ingin dicantumkan di PDF.
                </p>
              </div>
            </div>
          </div>

          {/* If Custom Selection Mode is active */}
          {exportMode === 'custom' && (
            <div className="border border-[#E6D5B8] bg-[#FFFDF9] rounded-2xl p-3.5 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex items-center justify-between gap-2">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    placeholder="Cari varian..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-8 pl-8 text-xs bg-white border-slate-200 rounded-xl"
                  />
                </div>
                <div className="flex items-center gap-1.5 shrink-0 text-[10px] font-bold">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="px-2 py-1 text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg cursor-pointer"
                  >
                    Semua
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className="px-2 py-1 text-slate-500 hover:bg-slate-100 rounded-lg cursor-pointer"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Checkbox List */}
              <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100">
                {filteredVariants.length === 0 ? (
                  <p className="text-[11px] text-slate-400 text-center py-4">Varian tidak ditemukan</p>
                ) : (
                  filteredVariants.map((v) => {
                    const isChecked = selectedVariants.has(v.name);
                    const sizesCount = v.sizes?.length || 0;
                    return (
                      <label
                        key={v.name}
                        onClick={() => toggleVariant(v.name)}
                        className={`flex items-center justify-between p-2 rounded-xl text-xs cursor-pointer transition-colors ${
                          isChecked ? 'bg-[#F9F5EC]/60 text-[#302621]' : 'hover:bg-slate-50 text-slate-600'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 pr-2">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleVariant(v.name)}
                            className="rounded-md border-slate-300 data-[state=checked]:bg-[#8C6208] data-[state=checked]:border-[#8C6208]"
                          />
                          <span className="font-bold text-xs truncate">{v.name}</span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-semibold shrink-0 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                          {sizesCount} Ukuran
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Status Selected Counter */}
              <div className="flex justify-between items-center text-[10px] text-slate-500 pt-2 border-t border-[#E6D5B8]/60">
                <span>Varian Terpilih:</span>
                <strong className="text-[#8C6208]">
                  {selectedVariants.size} dari {totalVariantsCount} Varian
                </strong>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="p-4 bg-[#F9F5EC] border-t border-[#E6D5B8] flex items-center justify-between gap-3 shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-[#E6D5B8] text-slate-600 hover:bg-white text-xs font-bold rounded-xl h-10 px-4"
          >
            Batal
          </Button>

          <Button
            type="button"
            onClick={handleExport}
            disabled={exportMode === 'custom' && selectedVariants.size === 0}
            className="flex-1 bg-[#8C6208] hover:bg-[#6e4c06] text-white font-bold text-xs rounded-xl h-10 gap-2 shadow-md shadow-[#8C6208]/20 cursor-pointer disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            <span>
              {exportMode === 'all' 
                ? `Buka & Export Semua Varian (${totalVariantsCount})` 
                : `Buka & Export ${selectedVariants.size} Varian`}
            </span>
            <ArrowRight className="w-3.5 h-3.5 ml-auto" />
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
